<script setup lang="ts">
/**
 * Jurisdiction Credits Flyout Component
 *
 * Displays a slide-out panel with detailed credit information for a course
 * across all jurisdictions. User's saved jurisdictions are highlighted.
 * Uses BaseFlyout for centralized accessibility and behavior patterns.
 *
 * Usage:
 * <JurisdictionFlyout
 *   :is-open="isOpen"
 *   :learning-object-id="learningObjectId"
 *   :credit-type="'CLE'"
 *   :jurisdictions="jurisdictionCredits"
 *   :user-jurisdictions="userJurisdictions"
 *   @close="handleClose"
 * />
 */
import { computed } from 'vue';
import BaseFlyout from './BaseFlyout.vue';
import { formatDate } from '@/utils/formatters';

/**
 * U10: the learner-facing 4-state taxonomy. Resolved server-side from the
 * internal accreditation status. The string `"Need to apply"` (and any
 * other internal-only state name) must not appear in any learner-facing
 * surface — that's the contract this type enforces at compile time.
 */
export type LearnerFacingCreditStatus =
  | 'Approved'
  | 'AvailableForApproval'
  | 'SelfApply'
  | 'NotAvailable';

/**
 * JurisdictionCredit - Data structure for displaying jurisdiction credit information.
 *
 * The flyout displays full jurisdiction names for readability (e.g., "New York").
 * Chips use abbreviations (e.g., "NY CLE 1.0") but the flyout should always show full names.
 */
export interface JurisdictionCredit {
  /** @deprecated Not used - full jurisdiction name is displayed instead */
  abbreviation: string;
  /** Full jurisdiction name for display (e.g., "New York") */
  jurisdiction: string;
  accredType: string;
  amount: number | string;
  creditType: string;
  validUntil?: string;
  /**
   * @deprecated U10: kept for legacy callers; learner-facing chip rendering
   * now keys on {@link learnerFacingStatus}. The internal status is no longer
   * surfaced to learners.
   */
  status?: string;
  /**
   * U10: resolved learner-facing status. Drives chip class, label, and body
   * copy. Optional only because some legacy call sites build the credit
   * rows from data that hasn't gone through the resolver yet (the flyout
   * falls back to `NotAvailable` in that case — safest learner-visible
   * default).
   */
  learnerFacingStatus?: LearnerFacingCreditStatus;
  isUserJurisdiction?: boolean; // Flag for user's saved jurisdiction (highlighted green at top)
}

export interface UserJurisdiction {
  jurisdictionId: number;
  jurisdictionName: string;
  jurisdictionAbbr: string;
  credentialType: string;
}

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    learningObjectId?: number | string;
    creditType?: string;
    jurisdictions: JurisdictionCredit[];
    userJurisdictions?: UserJurisdiction[];
    loading?: boolean;
    error?: string | null;
  }>(),
  {
    creditType: 'CLE',
    userJurisdictions: () => [],
    loading: false,
    error: null,
  }
);

const emit = defineEmits<{
  close: [];
}>();

const flyoutTitle = computed(() => `${props.creditType} Credits`);

// Check if a jurisdiction matches user's saved jurisdictions
// Supports both: 1) isUserJurisdiction flag on credit, 2) userJurisdictions prop comparison
function isUserJurisdiction(jur: JurisdictionCredit): boolean {
  // First check the direct flag (set from API's userMatch)
  if (jur.isUserJurisdiction) return true;

  // Fall back to comparing against userJurisdictions prop
  if (props.userJurisdictions && props.userJurisdictions.length > 0) {
    return props.userJurisdictions.some(
      (uj) =>
        uj.jurisdictionName === jur.jurisdiction &&
        uj.credentialType === jur.accredType
    );
  }

  return false;
}

// Separate user's jurisdictions from others (green highlight at top)
const userMatchedJurisdictions = computed(() => {
  return props.jurisdictions.filter((jur) => isUserJurisdiction(jur));
});

const otherJurisdictions = computed(() => {
  return props.jurisdictions.filter((jur) => !isUserJurisdiction(jur));
});

function handleClose() {
  emit('close');
}

/**
 * BPE-732: the column rendered `validUntil` straight, which was harmless only
 * because nothing populated it. Now that course lists pass the real date, it
 * has to be formatted the way every other credit surface formats it rather
 * than showing a raw ISO timestamp.
 */
function formatValidUntil(validUntil: string | undefined): string {
  return validUntil ? formatDate(validUntil) : 'N/A';
}

function formatCreditAmount(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return num.toFixed(1);
}

// Status configuration with icon, CSS class, label, body copy, and tooltip.
// U10: keyed on the learner-facing 4-state taxonomy, not on internal
// AccreditationStatus. Beth's authoritative 2026-05-06 mapping.
interface StatusConfig {
  icon: string;
  tagClass: string;
  label: string;
  /** Body copy shown beneath the chip (per U10 plan). */
  body: string;
  tooltip: string;
}

const statusConfigs: Record<LearnerFacingCreditStatus, StatusConfig> = {
  Approved: {
    icon: 'check',
    tagClass: '-success',
    label: 'Approved',
    body: 'Credit will be granted by your jurisdiction.',
    tooltip: 'Credit will be granted by your jurisdiction.',
  },
  AvailableForApproval: {
    icon: 'schedule',
    tagClass: '-warning',
    label: 'Available for Approval',
    body: 'BARBRI will request credit on your behalf. Approval is not guaranteed.',
    tooltip: 'BARBRI will request credit on your behalf. Approval is not guaranteed.',
  },
  SelfApply: {
    icon: 'person',
    tagClass: '-neutral',
    label: 'Self Apply',
    body: 'You must apply directly to your jurisdiction to claim this credit. BARBRI will provide your attendance certificate.',
    tooltip: 'You must apply directly to your jurisdiction to claim this credit. BARBRI will provide your attendance certificate.',
  },
  NotAvailable: {
    icon: 'close',
    tagClass: '-danger',
    label: 'Not Available',
    body: 'This course is not available for credit in your jurisdiction. You can still watch it for educational value.',
    tooltip: 'This course is not available for credit in your jurisdiction. You can still watch it for educational value.',
  },
};

// U10: read the learner-facing status from the credit row. When absent
// (legacy data path), default to NotAvailable — safest learner-visible
// fallback that honors the player-unlock contract.
function getStatusConfig(jur: JurisdictionCredit): StatusConfig {
  const key: LearnerFacingCreditStatus = jur.learnerFacingStatus ?? 'NotAvailable';
  return statusConfigs[key];
}
</script>

<template>
  <BaseFlyout
    :is-open="isOpen"
    :title="flyoutTitle"
    width="420px"
    @close="handleClose"
  >
    <!-- Loading State -->
    <div v-if="loading" class="flyout-loading">
      <bb-icon name="progress_activity" class="spinning" decorative></bb-icon>
      Loading...
    </div>

    <!-- Error State -->
    <bb-alert v-else-if="error" variant="error">{{ error }}</bb-alert>

    <!-- Empty State -->
    <div v-else-if="jurisdictions.length === 0" class="no-credits-message">
      No approved jurisdictions for this course.
    </div>

    <!-- Jurisdictions List -->
    <template v-else>
      <!-- User's Jurisdictions -->
      <template v-if="userMatchedJurisdictions.length > 0">
        <h3 class="credits-section-header">
          Your jurisdiction{{ userMatchedJurisdictions.length !== 1 ? 's' : '' }}
        </h3>
        <div
          v-for="(jur, index) in userMatchedJurisdictions"
          :key="`user-${index}`"
          class="credits-jurisdiction-card user-jurisdiction"
        >
          <div class="jurisdiction-name">
            <bb-icon name="check_circle" class="user-jur-icon" decorative></bb-icon>
            {{ jur.jurisdiction }}
          </div>
          <div class="jurisdiction-details">
            <div class="jurisdiction-col">
              <span class="col-label">Credits</span>
              <span class="col-value">{{ formatCreditAmount(jur.amount) }} {{ jur.creditType }}</span>
            </div>
            <div class="jurisdiction-col">
              <span class="col-label">Available until</span>
              <span class="col-value">{{ formatValidUntil(jur.validUntil) }}</span>
            </div>
            <div class="jurisdiction-col">
              <span class="col-label">Status</span>
              <span class="col-value">
                <span
                  :class="['bb-tag', getStatusConfig(jur).tagClass]"
                  :title="getStatusConfig(jur).tooltip"
                >
                  <bb-icon :name="getStatusConfig(jur).icon" decorative></bb-icon>{{ getStatusConfig(jur).label }}
                </span>
              </span>
            </div>
          </div>
          <!-- U10: body copy explains the credit pathway in plain language. -->
          <div class="jurisdiction-body">{{ getStatusConfig(jur).body }}</div>
        </div>
      </template>

      <!-- Other Jurisdictions -->
      <template v-if="otherJurisdictions.length > 0">
        <!-- Only show header if there are user jurisdictions above -->
        <h3 v-if="userMatchedJurisdictions.length > 0" class="credits-section-header">Other jurisdictions</h3>
        <div
          v-for="(jur, index) in otherJurisdictions"
          :key="`other-${index}`"
          class="credits-jurisdiction-card"
        >
          <div class="jurisdiction-name">
            {{ jur.jurisdiction }}
          </div>
          <div class="jurisdiction-details">
            <div class="jurisdiction-col">
              <span class="col-label">Credits</span>
              <span class="col-value">{{ formatCreditAmount(jur.amount) }} {{ jur.creditType }}</span>
            </div>
            <div class="jurisdiction-col">
              <span class="col-label">Available until</span>
              <span class="col-value">{{ formatValidUntil(jur.validUntil) }}</span>
            </div>
            <div class="jurisdiction-col">
              <span class="col-label">Status</span>
              <span class="col-value">
                <span
                  :class="['bb-tag', getStatusConfig(jur).tagClass]"
                  :title="getStatusConfig(jur).tooltip"
                >
                  <bb-icon :name="getStatusConfig(jur).icon" decorative></bb-icon>{{ getStatusConfig(jur).label }}
                </span>
              </span>
            </div>
          </div>
          <!-- U10: body copy explains the credit pathway in plain language. -->
          <div class="jurisdiction-body">{{ getStatusConfig(jur).body }}</div>
        </div>
      </template>
    </template>
  </BaseFlyout>
</template>

<style scoped>
/* Content-specific styles - flyout structure handled by BaseFlyout */
.flyout-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px 24px;
  color: var(--color-text-secondary);
}

/* Uses global @keyframes spin and .spinning from components.css */

.no-credits-message {
  text-align: center;
  padding: 48px 24px;
  color: var(--color-text-secondary);
}

.credits-section-header {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border-light);
}

.credits-section-header:not(:first-child) {
  margin-top: 24px;
}

.credits-jurisdiction-card {
  background: var(--color-bg-secondary, #f8f9fa);
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 12px;
}

.credits-jurisdiction-card:last-child {
  margin-bottom: 0;
}

.credits-jurisdiction-card.user-jurisdiction {
  background: var(--color-success-bg);
  border: 1px solid var(--color-success-border);
}

.jurisdiction-name {
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text-primary);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-jur-icon {
  color: var(--color-success, #16a34a);
  font-size: 20px;
}

.jurisdiction-details {
  display: grid;
  grid-template-columns: 1fr 1.5fr auto;
  gap: 16px;
}

.jurisdiction-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.col-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
}

.col-value {
  font-size: 14px;
  color: var(--color-text-primary);
}

/* U10: body copy under each jurisdiction card explaining the credit pathway. */
.jurisdiction-body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-light);
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-secondary);
}


/* Responsive: stack on narrow screens */
@media (max-width: 480px) {
  .jurisdiction-details {
    grid-template-columns: 1fr 1fr;
    gap: 12px 8px;
  }

  .jurisdiction-details .jurisdiction-col:last-child {
    grid-column: span 2;
  }
}

</style>
